import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Class } from '@app/models/class.model';
import { NoteUpdate } from '@app/models/note.model';
import { AccesslogService } from '@app/services/accesslog.service';
import { ApiService } from '@app/services/api.service';
import { DialogService } from '@app/services/dialog.service';
import { MessageService } from '@app/services/message.service';
import { RecordService } from '@app/services/record.service';
import { AuthSelectors } from '@app/store/auth/auth.selectors';
import { DataSelectors } from '@app/store/data/data.selectors';
import { LibNoteService } from '@care-connect-japan/ckweblib';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs';


const dummyClasses: Class[] = [
  {
    name: 'さくら', students: [
      { id: '1', name: '田中 太郎', imageUrl: '' },
      { id: '2', name: '山田 花子', imageUrl: '' },
      { id: '3', name: '佐藤 健太', imageUrl: '' },
      { id: '4', name: '鈴木 美咲', imageUrl: '' },
      { id: '5', name: '高橋 翔太', imageUrl: '' },
      { id: '6', name: '伊藤 さくら', imageUrl: '' }
    ], id: 1
  },
  {
    name: 'たんぽぽ', students: [
      { id: '7', name: '渡辺 優斗', imageUrl: '' },
      { id: '8', name: '中村 結衣', imageUrl: '' },
      { id: '9', name: '小林 大輝', imageUrl: '' },
      { id: '10', name: '加藤 陽菜', imageUrl: '' },
      { id: '11', name: '吉田 蓮', imageUrl: '' },
      { id: '12', name: '山本 七海', imageUrl: '' },
    ], id: 2
  },
  {
    name: 'ひまわり', students: [
      { id: '13', name: '松本 颯', imageUrl: '' },
      { id: '14', name: '井上 莉子', imageUrl: '' },
      { id: '15', name: '木村 陽翔', imageUrl: '' },
      { id: '16', name: '林 愛莉', imageUrl: '' },
      { id: '17', name: '斎藤 悠真', imageUrl: '' },
      { id: '18', name: '清水 心春', imageUrl: '' },
    ], id: 3
  },
  {
    name: 'たんぽぽ', students: [
      { id: '19', name: '森 大和', imageUrl: '' },
      { id: '20', name: '池田 咲良', imageUrl: '' },
      { id: '21', name: '石川 陸', imageUrl: '' },
      { id: '22', name: '前田 美羽', imageUrl: '' },
    ], id: 4
  }
];

@Component({
  selector: 'app-record-create',
  templateUrl: './record-create.component.html',
})
export class RecordCreateComponent implements OnInit {

  constructor(
    protected fb: FormBuilder,
    protected dialogService: DialogService,
    protected router: Router,
    protected store: Store,
    protected recordService: RecordService,
    protected libNoteService: LibNoteService,
    protected apiService: ApiService,
    protected message: MessageService,
    protected dialog: DialogService,
    protected snackBar: MatSnackBar,
    protected accesslog: AccesslogService
  ) { }

  classes = dummyClasses;
  classForms!: FormArray;
  searchClassStr: string = '';

  get allDirtyForms() {
    return this.classForms.controls.map(
      (cls) => {
        const student = cls.get('students') as FormArray;
        const dirties = student.controls.filter(s => s.dirty);
        return dirties;
      }
    ).flat();
  }
  get submittedForm() {
    return this.allDirtyForms.filter(
      (form) => form?.get('record')?.get('isSubmitted')?.value
    );
  }

  get dirtyForms() {
    return this.allDirtyForms.filter(
      (form) => !form?.get('record')?.get('isSubmitted')?.value
    );
  }

  get displayClasses() {
    if (!this.searchClassStr) return this.classForms.controls;
    let classes = this.classForms.controls.filter(x => (x.get('name')?.value as string).includes(this.searchClassStr));
    return classes;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initForm() {
    this.classForms = this.fb.array(
      this.classes.map(cls =>
        this.fb.group({
          name: [cls.name],
          students: this.fb.array(
            cls.students.map(student => this.fb.group({
              id: [student.id],
              name: [student.name],
              imageUrl: [student.imageUrl],
              record: this.fb.group({
                activity: [''],
                detail: [''],
                date: [''],
                images: [],
                // use for submited form control
                isSubmitted: [false]
              })
            }))
          )
        }) as FormGroup
      )
    );
  }

  initializeForm() {
    const objectcd = this.store.selectSnapshot(AuthSelectors.objectcd);
    const studentByUnits = this.store.selectSnapshot(DataSelectors.userList(objectcd));
    const classes = this.mapUnitsToClasses(studentByUnits);

    this.classForms = this.fb.array(
      classes.map(cls =>
        this.fb.group({
          name: [cls.name],
          students: this.fb.array(
            cls.students.map((student: any) => this.fb.group({
              id: [student.id],
              name: [student.name],
              imageUrl: [student.imageUrl],
              record: this.fb.group({
                isSubmitted: [false],
                isInitialized: [false],
                items: this.fb.array([])
              })
            }))
          )
        }) as FormGroup
      )
    );
  }

  onSubmit() {
    if (this.dirtyForms.length === 0) return;
    const data = this.dirtyForms.map((form) => {
      const value = form.value;
      return value;
    });

    const records = this.dirtyForms.flatMap((form) => form?.value?.record?.items);
    const noteDatas = this.libNoteService.getNoteData();
    if (this.recordService.isValidationError(noteDatas)) return;
    const findNoteData = this.recordService.findNoteData(noteDatas);
    const payload: NoteUpdate[] = [];
    records.forEach((record) => {
      const noteData: any = findNoteData(record.noteData);
      const operation = this.recordService.getOperation(noteData?.noteid);
      const note = {
        ...record.noteData,
        ...noteData,
        objkbn: '00',
        dataV2Contents: undefined,
        tp: undefined,
        titles: undefined,
      };
      payload.push({ 
        operation, 
        note, 
        displayName: 'Kids_記録一覧'
      });
    });
    this.apiService
      .setNoteList(payload)
      .pipe(filter((res) => !!res))
      .subscribe((res) => {
        // this.isLoading = false;
        const errorList = res
          .filter((item) => !!item.error)
          .map((item) => this.message.getErrorMessage(item.error!));

        if (errorList.length > 0) {
          this.dialog.message(errorList.join('\n'));
        } else if (res.length > 0) {
          this.snackBar.open('記録を保存しました');
        }

        if (errorList.length !== res.length) {
          // reset data 
          const msg = `${data.length}件の記録を保存しました`;
          const dialog = this.dialogService.recordSubmitPopup(msg);
          setTimeout(() => {
            dialog.afterClosed().subscribe(() => this.router.navigate(['']));
            dialog.close();
          }, 2000);
          // this.store.dispatch(new ScheduleAction.TriggerHistory());
          // this.redirect();
        }
      });
  }

  private mapUnitsToClasses(units: any[]): any[] {
    return units.map(unit => ({
      name: unit.name,
      students: unit.items.map((student: any) => {
        return {
          id: student.daicd,
          name: `${student.family} ${student.given}`,
          imageUrl: student.isImage ? 'has-image' : null
        }
      })
    }));
  }
}
